import React, { useState } from 'react';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import './DeviceSettingsTemplateModal.css';

const DEVICE_KIND_OPTIONS = [
  { label: 'iBeam', code: 'IL_IB' },
  { label: 'iLock Blue', code: 'IL_BX' },
  { label: 'Latch', code: 'IL_LA' },
];

const FIELD_TYPE_OPTIONS = [
  { label: 'Text', value: 'Text' },
  { label: 'Checkbox', value: 'Checkbox' },
  { label: 'Dropdown', value: 'Dropdown' },
  { label: 'Multi-select', value: 'Multi-select' },
  { label: 'RadioButton', value: 'Radio' },
];

const DATA_TYPE_OPTIONS = [
  { label: 'Text', value: 'text' },
  { label: 'Integer', value: 'integer' },
  { label: 'Boolean', value: 'Boolean' },
];

export default function DeviceSettingsTemplateModal({ isOpen, onClose }) {
  const [sections, setSections] = useState([
    { deviceCode: '', deviceKind: '', settingsName: '', fieldType: '', values: '', dataType: '' },
  ]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmIsError, setConfirmIsError] = useState(false);

  const updateSection = (idx, updates) => {
    setSections(prev => {
      const newSections = [...prev];
      newSections[idx] = { ...newSections[idx], ...updates };
      return newSections;
    });
  };

  const removeSection = (index) => {
    setSections(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddSection = () => {
    setSections(prev => [
      ...prev,
      { deviceCode: '', deviceKind: '', settingsName: '', fieldType: '', values: '', dataType: '' },
    ]);
  };

  const handleSubmit = async () => {
    const payloads = sections.map(sec => {
      const rawName = (sec.settingsName || '').trim();
      const codeFromName = rawName.toLowerCase().replace(/\s+/g, '_')
      .replace(/^_+|_+$/g, '');
      return {
        DeviceKind: sec.deviceKind,
        Code: codeFromName,
        Name: rawName,
        Values: sec.values.trim(),
        DataType: sec.dataType,
        FieldType: sec.fieldType,
      };
    });

    try {
      await apiClient.post('/device_settings_template', payloads);
      setConfirmMessage('Settings saved successfully!');
      setConfirmIsError(false);
      setShowConfirmModal(true);
    } catch (err) {
      console.error(err);
      setConfirmMessage('Failed to save template settings.');
      setConfirmIsError(true);
      setShowConfirmModal(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dst-overlay">
      <div className="dst-modal">
        <header className="dst-header">
          <h2>Device Settings Template</h2>
          <button className="dst-close" onClick={onClose}>X</button>
        </header>

        <div className="dst-body">
          {sections.map((sec, idx) => (
            <div key={idx}>
              <div className="dst-section">
                <input
                  type="text"
                  placeholder="Settings Name"
                  value={sec.settingsName}
                  onChange={e => updateSection(idx, { settingsName: e.target.value })}
                  className="dst-input"
                />

                <select
                  value={sec.deviceCode}
                  onChange={e => {
                    const code = e.target.value;
                    const device = DEVICE_KIND_OPTIONS.find(d => d.code === code);
                    if (device) {
                      updateSection(idx, {
                        deviceCode: code,
                        deviceKind: device.label,
                      });
                    }
                  }}
                  className="dst-select"
                >
                  <option value="">Select Device Type</option>
                  {DEVICE_KIND_OPTIONS.map(device => (
                    <option key={device.code} value={device.code}>
                      {device.label}
                    </option>
                  ))}
                </select>

                <select
                  value={sec.fieldType}
                  onChange={e => updateSection(idx, { fieldType: e.target.value })}
                  className="dst-select"
                >
                  <option value="">Select Field Type</option>
                  {FIELD_TYPE_OPTIONS.map(field => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Values (comma separated)"
                  value={sec.values}
                  onChange={e => updateSection(idx, { values: e.target.value })}
                  className="dst-input"
                />

                <select
                  value={sec.dataType}
                  onChange={e => updateSection(idx, { dataType: e.target.value })}
                  className="dst-select"
                >
                  <option value="">Select Data Type</option>
                  {DATA_TYPE_OPTIONS.map(dataType => (
                    <option key={dataType.value} value={dataType.value}>
                      {dataType.label}
                    </option>
                  ))}
                </select>
                <button 
                  className="dst-remove-section" 
                  onClick={() => removeSection(idx)}
                >
                  X
                </button>
              </div>
              {idx < sections.length - 1 && <hr className="dst-divider" />}
            </div>
          ))}
        </div>

        <footer className="dst-footer">
          <button className="dst-button dst-button--cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="dst-button dst-button--add" onClick={handleAddSection}>
            + Add more
          </button>
          <button className="dst-button dst-button--submit" onClick={handleSubmit}>
            Submit
          </button>
        </footer>
      </div>

      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={confirmMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={confirmIsError}
          onConfirm={() => {
            setShowConfirmModal(false);
            if (!confirmIsError) {
              onClose();
            }
          }}
        />
      )}
    </div>
  );
}